import React, { memo, useMemo } from 'react';
import AtlasMapComponent, { isStyledMapConfig } from '../AtlasMapComponent';
import AtlasMapRuntime from './atlasMapRuntime';
import AtlasLandRuntime from './atlasLandRuntime';
import AtlasMapLegend from './AtlasMapLegend';
import useAtlasTheme from '../useAtlasTheme';

/**
 * Map renderer for Atlas viz payloads.
 *   - styled configs (choropleth_map / data_point_map with meta.version >= 1) -> a card: title + finding,
 *     the legend strip, a fixed-height map box, and the footnote (same layout as the PDF card). The card grows; the map does not.
 *   - everything else -> the legacy fixed-height box exactly as before.
 * variant: 'chat' | 'brief' | 'shared' (brief variants keep the brief's wrapper classes).
 */
function AtlasMapView({ config, variant = 'chat' }) {
  const inBrief = variant === 'brief' || variant === 'shared';
  const styled = isStyledMapConfig(config);
  const theme = useAtlasTheme();
  const legendModel = useMemo(() => {
    if (!styled) return null;
    try {
      return config.type === 'land_map' ? AtlasLandRuntime.legendModel(config) : AtlasMapRuntime.legendModel(config, theme);
    } catch {
      return null;
    }
  }, [config, theme, styled]);
  if (!styled) {
    return (
      <div className={inBrief ? 'atlas-brief-viz atlas-brief-viz-map' : 'atlas-viz atlas-viz-map'}>
        <AtlasMapComponent mapConfig={config} />
      </div>
    );
  }
  const meta = config.meta || {};
  const title = meta.title || config.title || '';
  const subtitle = meta.subtitle || '';
  return (
    <div className={inBrief ? 'atlas-brief-viz atlas-brief-viz-map-card' : 'atlas-viz atlas-viz-map-card'}>
      {title || subtitle ? (
        <div className="atlas-map-card-head">
          {title ? <div className="atlas-map-card-title">{title}</div> : null}
          {subtitle ? <div className="atlas-map-card-sub">{subtitle}</div> : null}
        </div>
      ) : null}
      <AtlasMapLegend model={legendModel} layout="strip" />
      <div className="atlas-map-card-box">
        <AtlasMapComponent mapConfig={config} showLegend={false} />
      </div>
      {meta.footnote ? (
        <div className="atlas-map-card-foot">
          <span>{meta.footnote}</span>
        </div>
      ) : null}
    </div>
  );
}

export default memo(AtlasMapView);
